import axios from "axios";
import { RaspberryPi } from "../model/RaspberryPi";
import { Config } from "../config/config";

export interface PiCredentials {
  username: string;
  password: string;
  sshKey?: string;
}

export interface CreatePiRequest {
  name: string;
  serialNumber: string;
  ipAddress?: string;
  subdomain?: string;
  credentials: PiCredentials;
}

// Hilfsfunktion, um die korrekte API-URL sicherzustellen
function getApiEndpoint(path: string): string {
  const apiRoot = Config.apiRoot || '';
  // Stelle sicher, dass apiRoot mit /api endet
  const normalizedApiRoot = apiRoot.endsWith('/api') ? apiRoot : (apiRoot + '/api');
  
  // Normalisiere den Pfad (entferne führenden Slash)
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  
  const fullEndpoint = `${normalizedApiRoot}/${normalizedPath}`;
  console.log(`[API-DEBUG] Vollständiger API-Endpunkt für ${path}: ${fullEndpoint}`);
  return fullEndpoint;
}

// API-Endpunkt für Raspberry Pi-Operationen - verwende die Hilfsfunktion
const PIS_ENDPOINT = getApiEndpoint('raspberry-pis');
console.log(`[API-CONFIG] Raspberry Pi API-Endpunkt konfiguriert als: ${PIS_ENDPOINT}`);

/**
 * Service für Raspberry Pi-bezogene API-Operationen
 */
export class PiService {
  /**
   */
  static generateWireguardConfig(): {
    wireguardPort: number;
    wireguardPresharedKey: null;
    wireguardClientPublicKey: null;
    wireguardClientPrivateKey: null;
    wireguardServerPrivateKey: null;
    wireguardServerPublicKey: null;
    bridgeNetworkBlock: string;
  } {
    // Keine vorläufigen Schlüssel, nur Standardwerte für Port und Netzwerkblock
    console.log('PiService: Warte auf Backend-generierte WireGuard-Schlüssel...');
    return {
      wireguardPort: 51820, // Standard WireGuard-Port
      bridgeNetworkBlock: '10.13.13.0/24', // Standardwert für Netzwerkblock
      // Alle Schlüssel sind initial NULL, um anzuzeigen, dass sie noch nicht generiert wurden
      wireguardPresharedKey: null,
      wireguardClientPublicKey: null,
      wireguardClientPrivateKey: null,
      wireguardServerPrivateKey: null,
      wireguardServerPublicKey: null
    };
  }

  /**
   * Generiert eine einzigartige Subdomain für einen Raspberry Pi
   * @param name Name des Raspberry Pi (wird nicht mehr verwendet)
   * @param serialNumber Seriennummer des Raspberry Pi (wird nicht mehr verwendet)
   */
  static generateSubdomain(name: string, serialNumber: string): string {
    // Generiere 8 zufällige alphanumerische Zeichen
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    
    // Generiere 8 zufällige Zeichen
    for (let i = 0; i < 8; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters.charAt(randomIndex);
    }
    
    // Keine Präfixe mehr, nur die zufälligen Zeichen
    return result;
  }

  /**
   * Holt alle Raspberry Pis vom Server
   */
  static async getPis(assigned?: boolean): Promise<RaspberryPi[]> {
    try {
      // Parameterobjekt erstellen, nur wenn der Filter definiert ist
      const params: { assigned?: boolean } = {};
      if (assigned !== undefined) {
        params.assigned = assigned;
      }
      
      console.log(`[API] GET ${PIS_ENDPOINT}`, { params }); // Verwende PIS_ENDPOINT

      const token = localStorage.getItem('token');
      const response = await axios.get<RaspberryPi[]>(PIS_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: params // Füge Parameter zur Anfrage hinzu
      });
      
      // Reduziere übermäßiges Logging
      console.log(`${response.data.length} Raspberry Pis vom Server erhalten.`);
      
      // Normalisiere jedes Pi-Objekt
      const normalizedPis = response.data.map((pi: any) => this.normalizePiData(pi));
      
      // Sortiere nach Erstellungsdatum absteigend (neueste zuerst)
      normalizedPis.sort((a: RaspberryPi, b: RaspberryPi) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA; // Absteigend (neueste zuerst)
      });
      
      return normalizedPis;
    } catch (error) {
      console.error("Fehler beim Laden der Raspberry Pis:", error);
      throw error;
    }
  }

  /**
   * Ruft nur verfügbare (nicht zugewiesene) Raspberry Pis ab.
   */
  static async getAvailableRaspberryPis(): Promise<RaspberryPi[]> {
    // Ruft getPis mit dem Filter assigned=false auf
    return this.getPis(false);
  }

  /**
   * Normalisiert die Datenstruktur eines Raspberry Pi-Objekts
   * Stellt sicher, dass sowohl id als auch _id gesetzt sind und alle erwarteten Felder vorhanden sind
   */
  static normalizePiData(pi: any): RaspberryPi {
    // Stelle sicher, dass sowohl id als auch _id vorhanden sind
    const normalizedPi = {
      ...pi,
      id: pi.id || pi._id,
      _id: pi._id || pi.id,
    };
    
    // Stelle sicher, dass die WireGuard-Felder existieren, aber setze nicht alle auf leere Strings
    // Behalte die vorhandenen Werte bei, auch wenn sie leere Strings sind
    if (typeof normalizedPi.wireguardClientPublicKey === 'undefined') {
      normalizedPi.wireguardClientPublicKey = pi.wireguardClientPublicKey || null;
    }
    
    if (typeof normalizedPi.wireguardClientPrivateKey === 'undefined') {
      normalizedPi.wireguardClientPrivateKey = pi.wireguardClientPrivateKey || null;
    }
    
    if (typeof normalizedPi.wireguardServerPublicKey === 'undefined') {
      normalizedPi.wireguardServerPublicKey = pi.wireguardServerPublicKey || null;
    }
    
    if (typeof normalizedPi.wireguardServerPrivateKey === 'undefined') {
      normalizedPi.wireguardServerPrivateKey = pi.wireguardServerPrivateKey || null;
    }
    
    if (typeof normalizedPi.wireguardPresharedKey === 'undefined') {
      normalizedPi.wireguardPresharedKey = pi.wireguardPresharedKey || null;
    }
    
    // Standardwerte für Port und Netzwerkblock
    normalizedPi.wireguardPort = pi.wireguardPort || 51820;
    normalizedPi.bridgeNetworkBlock = pi.bridgeNetworkBlock || '10.13.13.0/24';
    
    // Stelle sicher, dass credentials immer existiert
    if (!normalizedPi.credentials) {
      normalizedPi.credentials = {
        username: '',
        password: '',
        sshKey: ''
      };
    }
    
    // Stelle sicher, dass isPrepared korrekt behandelt wird (entweder der Wert vom Server oder false)
    // Wichtig: Wir müssen den Wert explizit als Boolean behandeln, damit false nicht als undefined interpretiert wird
    normalizedPi.isPrepared = pi.isPrepared === true;
    
    // Vereinfachtes Logging mit nur den relevanten Informationen
    const hasClientKey = Boolean(normalizedPi.wireguardClientPublicKey);
    const hasServerKey = Boolean(normalizedPi.wireguardServerPublicKey);
    
    if (!hasClientKey || !hasServerKey) {
      console.log(`Pi ${normalizedPi._id} (${normalizedPi.name}): WireGuard-Konfiguration unvollständig. ClientKey: ${hasClientKey}, ServerKey: ${hasServerKey}`);
    }
    
    return normalizedPi;
  }

  /**
   * Holt einen einzelnen Raspberry Pi anhand seiner ID
   * Wenn die WireGuard-Konfiguration unvollständig ist, wird versucht, die vollständigen Schlüssel zu erhalten
   */
  static async getPiById(id: string): Promise<RaspberryPi> {
    try {
      // Validierung der ID
      if (!id || id === 'undefined') {
        console.error('[PiService] Invalid or missing Pi ID for getPiById');
        throw new Error('Eine gültige Raspberry Pi ID wird benötigt');
      }
      
      console.log(`[PiService] Getting Pi by ID: ${id}`);
      const token = localStorage.getItem('token');
      const endpoint = `${PIS_ENDPOINT}/${id}`;
      console.log(`[PiService] API endpoint: ${endpoint}`);
      
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log(`[PiService] Successfully loaded Pi with ID: ${id}`);
      
      // Normalisiere die Daten
      const normalizedPi = this.normalizePiData(response.data);
      
      // Prüfe, ob die WireGuard-Konfiguration vollständig ist
      const hasClientKey = Boolean(normalizedPi.wireguardClientPublicKey);
      const hasServerKey = Boolean(normalizedPi.wireguardServerPublicKey);
      
      if (!hasClientKey || !hasServerKey) {
        console.log(`[PiService] WireGuard config incomplete for Pi ${id}. Retrying in 2 seconds...`);
        
        // Wenn die Konfiguration unvollständig ist, versuche es nach kurzer Verzögerung erneut
        // Dies gibt dem Backend Zeit, die Schlüssel zu generieren
        return new Promise((resolve) => {
          setTimeout(async () => {
            try {
              // Erneut versuchen, den Pi mit vollständigen Schlüsseln zu laden
              const refreshedPi = await this.getPiById(id);
              resolve(refreshedPi);
            } catch (err) {
              // Bei einem Fehler geben wir den Pi mit den unvollständigen Schlüsseln zurück
              console.warn(`[PiService] Could not get complete WireGuard keys for Pi ${id}. Using incomplete data.`);
              resolve(normalizedPi);
            }
          }, 2000); // 2 Sekunden Verzögerung
        });
      }
      
      return normalizedPi;
    } catch (error: any) {
      console.error(`[PiService] Error fetching Pi with ID ${id}:`, error);
      if (error.response) {
        console.error(`[PiService] Response status: ${error.response.status}, data:`, error.response.data);
      }
      throw error;
    }
  }

  /**
   * Holt einen Raspberry Pi anhand seiner Seriennummer vom Server
   * @param serialNumber Seriennummer des Raspberry Pi (beginnt mit "RP")
   */
  static async getPiBySerialNumber(serialNumber: string): Promise<RaspberryPi> {
    try {
      // Validiere die Seriennummer
      if (!serialNumber || serialNumber.trim() === '') {
        throw new Error('Keine Seriennummer angegeben');
      }
      
      // Seriennummer sollte mit RP beginnen und dann Ziffern enthalten
      if (!/^RP\d+$/.test(serialNumber.trim())) {
        console.warn(`Ungewöhnliches Seriennummernformat: ${serialNumber}`);
      }
      
      // Prüfe, ob ein gültiges Auth-Token vorhanden ist
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Nicht autorisiert. Bitte melden Sie sich an.');
      }
      
      // Sende Anfrage an das Backend
      const response = await axios.get(`${PIS_ENDPOINT}/serial/${serialNumber.trim()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.status === 200 && response.data) {
        console.log(`Pi mit Seriennummer ${serialNumber} gefunden.`);
        
        // Normalisiere die Daten
        const normalizedPi = this.normalizePiData(response.data);
        return normalizedPi;
      } else {
        throw new Error('Raspberry Pi mit dieser Seriennummer wurde nicht gefunden');
      }
    } catch (error: any) {
      console.error('Fehler beim Abrufen des Raspberry Pi:', error);
      
      if (error.response) {
        if (error.response.status === 404) {
          throw new Error('Raspberry Pi mit dieser Seriennummer wurde nicht gefunden');
        }
        throw new Error(error.response.data.message || 'Serverfehler beim Abrufen des Raspberry Pi');
      }
      
      throw error;
    }
  }

  /**
   * Erstellt einen neuen Raspberry Pi
   */
  static async createPi(piData: Omit<RaspberryPi, '_id' | 'createdAt' | 'updatedAt'>): Promise<RaspberryPi> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Nicht autorisiert. Bitte melden Sie sich an.');
      }
      
      // Stelle sicher, dass die minimalen Anforderungen erfüllt sind
      const requiredFields = ['name', 'serialNumber', 'credentials'];
      for (const field of requiredFields) {
        if (!piData[field as keyof typeof piData]) {
          throw new Error(`Feld ${field} ist erforderlich`);
        }
      }
      
      // Setze den Status auf 'created', wenn er nicht angegeben ist
      const dataToSend = {
        ...piData,
        status: piData.status || 'created'
      };
      
      // Sende die Anfrage an das Backend
      const response = await axios.post(PIS_ENDPOINT, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Normalisiere die Antwort und gib das normalisierte Pi-Objekt zurück
      const newPi = this.normalizePiData(response.data);
      
      console.log(`Neuer Raspberry Pi erstellt: ${newPi.name} (ID: ${newPi._id})`);
      
      // Warte kurz und versuche dann, die vollständigen Daten zu laden
      // Dies ist wichtig, da das Backend möglicherweise asynchron die WireGuard-Konfiguration generiert
      console.log('Warte 2 Sekunden auf die vollständige WireGuard-Konfiguration...');
      
      // Verzögere die Antwort, damit das Backend Zeit hat, die WireGuard-Konfiguration zu generieren
      return new Promise((resolve) => {
        setTimeout(async () => {
          try {
            // Versuche, die vollständigen Daten zu laden
            const completePi = await this.getPiById(newPi._id);
            resolve(completePi);
          } catch (err) {
            // Bei einem Fehler geben wir die ursprünglichen Daten zurück
            console.warn(`Konnte keine vollständigen WireGuard-Daten laden für Pi ${newPi._id}. Verwende vorläufige Daten.`);
            resolve(newPi);
          }
        }, 2000); // 2 Sekunden Verzögerung
      });
    } catch (err: any) {
      console.error('Fehler beim Erstellen des Raspberry Pi:', err);
      
      // Verbesserte Fehlerbehandlung, um detaillierte Informationen aus dem Backend zu erhalten
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response headers:', err.response.headers);
        console.error('Response data:', JSON.stringify(err.response.data, null, 2));
        
        // Bei 400-Fehler genauer untersuchen
        if (err.response.status === 400) {
          // Extrahiere die Fehlermeldung aus den Validierungsfehlern
          let errorMessage = 'Validierungsfehler';
          
          // Wenn es spezifische Validierungsfehler gibt, zeige den ersten an
          if (err.response.data.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
            const firstError = err.response.data.errors[0];
            errorMessage = `Validierungsfehler: ${firstError.msg || 'Unbekannter Fehler'}`;
            console.error('Spezifische Fehler:', JSON.stringify(err.response.data.errors, null, 2));
          } else if (err.response.data.message) {
            // Wenn eine allgemeine Fehlermeldung vorhanden ist, verwende diese
            errorMessage = `Validierungsfehler: ${err.response.data.message}`;
          }
          
          throw new Error(`API-Validierungsfehler: ${errorMessage}`);
        }
      }
      
      throw err;
    }
  }

  /**
   * Aktualisiert einen vorhandenen Raspberry Pi
   */
  static async updatePi(id: string, piData: Partial<RaspberryPi>): Promise<RaspberryPi> {
    try {
      // Debug-Logging für die Aktualisierung
      console.log(`Aktualisiere Pi ${id} mit Daten:`, JSON.stringify(piData));
      
      const token = localStorage.getItem('token');
      // Verwende PATCH anstelle von PUT, da das Backend nur PATCH-Endpunkte unterstützt
      const response = await axios.patch(`${PIS_ENDPOINT}/${id}`, piData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Debug-Logging für die Antwort
      console.log(`Server-Antwort für Pi ${id}:`, JSON.stringify(response.data));
      
      // Normalisiere die Antwort, um sicherzustellen, dass alle Felder korrekt sind
      return this.normalizePiData(response.data);
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Raspberry Pi mit ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Weist einen Raspberry Pi einem Server zu
   */
  static async assignToServer(piId: string, serverId: string): Promise<RaspberryPi> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${PIS_ENDPOINT}/${piId}/assign-server/${serverId}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Zuweisen des Raspberry Pi zu Server:`, error);
      throw error;
    }
  }

  /**
   * Hilfsfunktion, um den API-Endpunkt zu testen
   * Dies kann helfen, API-Probleme zu diagnostizieren
   */
  static async checkApiEndpoint(): Promise<boolean> {
    try {
      console.log(`Testing API endpoint: ${PIS_ENDPOINT}`);
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token available for API check');
        return false;
      }
      
      // Probe-Anfrage senden
      const response = await axios.get(`${PIS_ENDPOINT}/ping`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`API endpoint check result:`, response.status, response.data);
      return true;
    } catch (error: any) {
      if (error.response) {
        // Die Anfrage wurde gesendet und der Server hat mit einem Status-Code 
        // außerhalb des Bereichs 2xx geantwortet
        console.error(`API endpoint check failed with status ${error.response.status}:`, 
          error.response.data);
        return error.response.status < 500;  // Wenn es kein 5xx Fehler ist, ist der API möglicherweise erreichbar
      } else if (error.request) {
        // Die Anfrage wurde gesendet, aber es ist keine Antwort eingegangen
        console.error('API endpoint unreachable - no response:', error.request);
        return false;
      } else {
        // Beim Einrichten der Anfrage ist ein Fehler aufgetreten
        console.error('API endpoint check error:', error.message);
        return false;
      }
    }
  }

  /**
   * Ordnet einen Raspberry Pi einem Besitzer zu
   * @param piId ID des Raspberry Pi
   * @param ownerId ID des neuen Besitzers
   */
  static async assignToOwner(piId: string, ownerId: string): Promise<RaspberryPi> {
    try {
      console.log(`[PiService] assignToOwner called with piId=${piId}, ownerId=${ownerId}`);
      
      // Validierung der Eingaben
      if (!piId || piId === 'undefined') {
        const error = 'Invalid Pi ID for assignment';
        console.error(`[PiService] ${error}`);
        throw new Error(error);
      }
      
      if (!ownerId || ownerId === 'undefined') {
        const error = 'Invalid owner ID for assignment';
        console.error(`[PiService] ${error}`);
        throw new Error(error);
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated. Please log in.');
      }

      // Try a direct update PATCH approach first - PUT is not supported by the backend
      try {
        console.log(`[PiService] Trying direct PATCH update approach to set owner ID`);
        const updateEndpoint = `${PIS_ENDPOINT}/${piId}`;
        console.log(`[PiService] PATCH endpoint: ${updateEndpoint}`);
        
        // Create the request payload
        const requestPayload = { 
          ownerId: ownerId,
          owner: ownerId, // Include both potential property names
        };
        
        console.log(`[PiService] PATCH request payload:`, requestPayload);
        
        const response = await axios.patch<RaspberryPi>(
          updateEndpoint,
          requestPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Log response to debug
        console.log(`[PiService] Direct update response:`, {
          status: response.status,
          piId: response.data._id,
          data: response.data,
          ownerId: response.data.ownerId || "Not returned in response"
        });
        
        // Normalisiere die Daten
        const normalizedPi = this.normalizePiData(response.data);
        
        // Explicitly fetch the Pi again to verify the update was successful
        try {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Longer delay
          console.log(`[PiService] Fetching Pi again to verify assignment`);
          const updatedPi = await this.getPiById(piId);
          console.log(`[PiService] Verification fetch complete Pi data:`, updatedPi);
          console.log(`[PiService] Verification fetch result:`, {
            piId: updatedPi._id,
            ownerId: updatedPi.ownerId || "Not set",
            ownerName: updatedPi.ownerName || "Not set"
          });
          
          // Also explicitly check if this Pi appears in the owner's Pis list
          try {
            console.log(`[PiService] Checking if Pi appears in owner's Pis list`);
            const ownerPis = await this.getPiForOwner(ownerId);
            const foundPi = ownerPis.find(pi => pi._id === piId);
            
            if (foundPi) {
              console.log(`[PiService] Pi found in owner's Pis list - assignment confirmed`);
              // Use this Pi data as it has the correct owner info
              return foundPi;
            } else {
              console.log(`[PiService] Pi NOT found in owner's Pis list - assignment may have failed`);
            }
          } catch (ownerPisError) {
            console.error(`[PiService] Error checking owner's Pis list:`, ownerPisError);
          }
          
          // Return the Pi we fetched for verification if it has owner info
          if (updatedPi.ownerId === ownerId) {
            return updatedPi;
          }
        } catch (verifyError) {
          console.error(`[PiService] Verification fetch failed:`, verifyError);
        }
        
        return normalizedPi;
      } catch (directUpdateError: any) {
        console.error(`[PiService] Direct update approach failed:`, directUpdateError);
        
        // Fall back to the existing endpoint methods
        // Try with different endpoint formats, starting with the most likely one to work
        let endpoint = `${PIS_ENDPOINT}/${piId}/assign-owner`;
        console.log(`[PiService] Falling back to API endpoint format 1 (assign-owner): ${endpoint}`);
        
        const assignPayload = { 
          ownerId, 
          accountId: ownerId,  // Include both formats that might be expected
          owner: ownerId       // Add this third possible property name
        };
        
        console.log(`[PiService] assign-owner request payload:`, assignPayload);
        
        try {
          const response = await axios.patch<RaspberryPi>(
            endpoint,
            assignPayload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          // Log response to debug
          console.log(`[PiService] Assignment response:`, {
            status: response.status,
            piId: response.data._id,
            data: response.data,
            ownerId: response.data.ownerId || "Not returned in response"
          });
          
          // Normalisiere die Daten
          const normalizedPi = this.normalizePiData(response.data);
          
          console.log(`[PiService] Assignment result after normalization:`, {
            piId: normalizedPi._id,
            ownerId: normalizedPi.ownerId,
            expectedOwnerId: ownerId,
            match: normalizedPi.ownerId === ownerId
          });
          
          // Verify the assignment directly
          try {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Longer delay
            const ownerPis = await this.getPiForOwner(ownerId);
            const foundPi = ownerPis.find(pi => pi._id === piId);
            
            if (foundPi) {
              console.log(`[PiService] Pi found in owner's Pis list after fallback - assignment confirmed`);
              return foundPi;
            }
          } catch (verifyError) {
            console.error(`[PiService] Verification after fallback failed:`, verifyError);
          }
          
          return normalizedPi;
        } catch (error: any) {
          // If the first format fails with a 404 or 400, try other formats
          if (error.response && (error.response.status === 404 || error.response.status === 400)) {
            console.log(`[PiService] Format 1 failed with ${error.response.status}, trying format 2`);
            
            // 2. POST with owner ID in body - trying POST instead of PATCH
            endpoint = `${PIS_ENDPOINT}/${piId}/assign`;
            console.log(`[PiService] Trying API endpoint format 2 with POST (assign): ${endpoint}`);
            
            try {
              const response = await axios.post<RaspberryPi>(
                endpoint,
                { ownerId, owner: ownerId, accountId: ownerId },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
              
              const normalizedPi = this.normalizePiData(response.data);
              console.log(`[PiService] Format 2 successful:`, {
                piId: normalizedPi._id,
                ownerId: normalizedPi.ownerId
              });
              
              return normalizedPi;
            } catch (error2: any) {
              // Final attempt - just try a regular update with complete Pi data
              if (error2.response && (error2.response.status === 404 || error2.response.status === 400)) {
                console.log(`[PiService] Format 2 failed, trying final approach - get Pi and update with owner`);
                
                try {
                  // First get the current Pi data
                  const currentPi = await this.getPiById(piId);
                  console.log(`[PiService] Retrieved current Pi data:`, currentPi);
                  
                  // Create a new object that maintains all properties but removes owner-related fields
                  // Note: 'owner' might not be in the type definition but could be in the actual data
                  const { ownerId, ownerName, ...piWithoutOwner } = currentPi;
                  
                  // Update the Pi with the modified data
                  console.log(`[PiService] Updating Pi with owner fields removed`);
                  const updatedPi = await this.updatePi(piId, piWithoutOwner);
                  console.log(`[PiService] Final approach result:`, updatedPi);
                  
                  return updatedPi;
                } catch (finalError) {
                  console.error(`[PiService] Final approach failed:`, finalError);
                  throw finalError;
                }
              } else {
                // If it's not a 404 or 400, rethrow the second error
                throw error2;
              }
            }
          } else {
            // If it's not a 404 or 400, rethrow the original error
            throw error;
          }
        }
      }
    } catch (error: any) {
      console.error(`[PiService] Error assigning Pi to owner:`, error);
      
      // Log detailed error information
      if (error.response) {
        console.error(`[PiService] Error response:`, {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
      
      throw error;
    }
  }
  
  /**
   * Entfernt die Zuweisung eines Raspberry Pi zu einem Server
   */
  static async unassignFromServer(piId: string): Promise<RaspberryPi> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${PIS_ENDPOINT}/${piId}/unassign-server`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Entfernen der Zuweisung des Raspberry Pi von Server:`, error);
      throw error;
    }
  }

  /**
   * Holt alle Raspberry Pis für einen bestimmten Besitzer
   * @param ownerId Die ID des Besitzer-Accounts
   */
  static async getPiForOwner(ownerId: string): Promise<RaspberryPi[]> {
    try {
      // Validiere die Account-ID
      if (!ownerId || ownerId.trim() === '' || ownerId === 'account1') {
        console.warn(`Invalid account ID for Pi lookup: "${ownerId}"`);
        return [];
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Not authenticated for Pi lookup');
        return [];
      }

      console.log(`[PiService] Getting Pis for owner ID: ${ownerId}`);
      const endpoint = `${PIS_ENDPOINT}/owner/${ownerId}`;
      console.log(`[PiService] API endpoint: ${endpoint}`);
      
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (Array.isArray(response.data)) {
        console.log(`[PiService] Found ${response.data.length} Pis for owner ID: ${ownerId}`);
        
        if (response.data.length > 0) {
          console.log(`[PiService] First Pi details:`, {
            id: response.data[0]._id,
            name: response.data[0].name,
            status: response.data[0].status
          });
        }
        
        response.data.forEach(pi => {
          if (pi && pi._id) {
            pi.id = pi._id;
          }
        });
      } else {
        console.warn(`[PiService] Unexpected response format:`, typeof response.data);
      }
      
      return response.data;
    } catch (error: any) {
      // Für 400 Bad Request geben wir einen leeren Array zurück
      if (error.response && error.response.status === 400) {
        console.warn(`[PiService] No Pis found or invalid request for owner ID: ${ownerId}`);
        return [];
      }
      console.error(`[PiService] Error fetching Pis for owner ID: ${ownerId}`, error);
      if (error.response) {
        console.error(`[PiService] Response status: ${error.response.status}, data:`, error.response.data);
      }
      throw error;
    }
  }

  /**
   * Entfernt die Zuweisung eines Raspberry Pi zu einer Waschstraße
   */
  static async unassignFromOwner(piId: string): Promise<RaspberryPi> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated. Please sign in.');
      }

      console.log(`[PiService] Unassigning Pi ${piId} from owner using PATCH method`);
      
      // Create a PATCH request with explicit null values
      // The backend expects a PATCH with null values to remove associations
      const updateData = {
        owner: null,    // Mongoose reference field
        ownerId: null,  // Frontend field
        ownerName: null // Frontend field
      };
      
      console.log(`[PiService] Sending data to remove owner:`, updateData);
      
      // Make a direct PATCH request to the Pi endpoint
      const endpoint = `${PIS_ENDPOINT}/${piId}`;
      const response = await axios.patch(endpoint, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`[PiService] Unassignment response:`, {
        status: response.status,
        hasOwner: !!response.data.ownerId
      });
      
      // Normalize and return the updated Pi
      return this.normalizePiData(response.data);
    } catch (error: any) {
      console.error(`[PiService] Error unassigning Pi:`, error);
      
      // Provide more details about the error for debugging
      if (error.response) {
        console.error(`[PiService] Response error details:`, {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  }

  /**
   * Generiert neue Anmeldeinformationen für einen Raspberry Pi
   * @param piId Die ID des Raspberry Pi
   * @returns Die neuen Anmeldeinformationen (Benutzername, Passwort, SSH-Key)
   */
  static async regenerateCredentials(piId: string): Promise<PiCredentials> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Nicht authentifiziert. Bitte melden Sie sich an.');
      }

      const response = await axios.post(`${PIS_ENDPOINT}/${piId}/regenerate-credentials`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data;
    } catch (error) {
      console.error(`Fehler beim Generieren neuer Anmeldeinformationen für Pi ${piId}:`, error);
      throw error;
    }
  }

  /**
   * Löscht einen Raspberry Pi
   * @param piId Die ID des Raspberry Pi
   */
  static async deletePi(piId: string): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Nicht authentifiziert. Bitte melden Sie sich an.');
      }

      await axios.delete(`${PIS_ENDPOINT}/${piId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error(`Fehler beim Löschen des Raspberry Pi ${piId}:`, error);
      throw error;
    }
  }

  /**
   * Aktualisiert den Vorbereitungsstatus eines Raspberry Pi
   * @param piId Die ID des Raspberry Pi
   * @param isPrepared Der neue Vorbereitungsstatus
   */
  static async updatePiPreparedStatus(piId: string, isPrepared: boolean): Promise<RaspberryPi> {
    try {
      if (!piId || piId === 'undefined') {
        console.error('Ungültige Pi-ID beim Aktualisieren des Vorbereitungsstatus');
        throw new Error('Eine gültige Raspberry Pi ID wird benötigt');
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Nicht authentifiziert. Bitte melden Sie sich an.');
      }
      
      console.log(`Aktualisiere Vorbereitungsstatus für Pi ${piId} auf ${isPrepared ? 'vorbereitet' : 'nicht vorbereitet'}`);
      
      // Verwende die bestehende updatePi-Methode und aktualisiere nur das isPrepared-Feld
      return this.updatePi(piId, { isPrepared });
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Vorbereitungsstatus für Pi ${piId}:`, error);
      throw error;
    }
  }

  /**
   * Aktualisiert den Status eines Raspberry Pi
   * @param piId ID des Raspberry Pi
   * @param status Neuer Status
   */
  static async updatePiStatus(piId: string, status: RaspberryPi['status']): Promise<RaspberryPi> {
    try {
      console.log(`Aktualisiere Status des Pi ${piId} auf ${status}`);
      
      // Verwende die bestehende updatePi-Methode und aktualisiere nur das Status-Feld
      return this.updatePi(piId, { status });
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Pi-Status:`, error);
      throw error;
    }
  }
}